import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth-context.js';
import { QueryClientProvider } from '@tanstack/react-query';
import { publicQueryClient } from './lib/query-client.js';

import { LoginPage } from './pages/LoginPage.js';
import { AcceptInvitePage } from './pages/AcceptInvitePage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { ClientsPage } from './pages/ClientsPage.js';
import { DocumentsPage } from './pages/DocumentsPage.js';
import { ClientDetailPage } from './pages/ClientDetailPage.js';
import { EngagementsPage } from './pages/EngagementsPage.js';
import { CompliancePage } from './pages/CompliancePage.js';
import { ComplianceItemDetailPage } from './pages/ComplianceItemDetailPage.js';
import { NotificationsPage } from './pages/NotificationsPage.js';
import { TeamsPage } from './pages/TeamsPage.js';
import { AttendancePage } from './pages/AttendancePage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { EngagementDetailPage } from './pages/EngagementDetailPage.js';
import { ProposalsPage } from './pages/ProposalsPage.js';
import { ProposalDetailPage } from './pages/ProposalDetailPage.js';
import { InvoicesPage } from './pages/InvoicesPage.js';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage.js';
import { PublicProposalPage } from './pages/PublicProposalPage.js';
import { PublicInvoicePage } from './pages/PublicInvoicePage.js';
import { AppDownloadPage } from './pages/AppDownloadPage.js';
import { MyWorkPage } from './pages/MyWorkPage.js';
import { MeetingsPage } from './pages/MeetingsPage.js';
import { HubPage } from './pages/HubPage.js';
import { TimeEntriesPage } from './pages/TimeEntriesPage.js';
import { GoalsPage } from './pages/GoalsPage.js';
import { TagsPage } from './pages/TagsPage.js';
import { WebhooksPage } from './pages/WebhooksPage.js';
import { AutomationsPage } from './pages/AutomationsPage.js';
import { TemplatesPage } from './pages/TemplatesPage.js';
import { InvoiceBrandingPage } from './pages/admin/InvoiceBrandingPage.js';
import { ProposalBrandingPage } from './pages/admin/ProposalBrandingPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { AdminLayout } from './pages/admin/AdminLayout.js';
import { AccessControlPage } from './pages/admin/AccessControlPage.js';
import { NotificationPolicyPage } from './pages/admin/NotificationPolicyPage.js';
import { GoalConfigPage } from './pages/admin/GoalConfigPage.js';
import { DashboardBuilderPage } from './pages/admin/DashboardBuilderPage.js';
import { AuditLogPage } from './pages/admin/AuditLogPage.js';
import { TrashPage } from './pages/admin/TrashPage.js';
import { ClauseLibraryPage } from './pages/admin/ClauseLibraryPage.js';
import { FreeZoneCatalogPage } from './pages/admin/FreeZoneCatalogPage.js';
import { ProposalComposerPage } from './pages/ProposalComposerPage.js';
import { Shell } from './components/Shell.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';

export function App() {
  const { user, loading, privacyEpoch } = useAuth();
  const { pathname } = useLocation();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-bg-base text-white">Loading…</div>;
  }
  // Public token routes must work for logged-in staff too (e.g. opening a
  // public proposal/invoice link from a detail page), so handle them before the
  // auth split.
  if (pathname.startsWith('/proposals/public/') || pathname.startsWith('/pay/') || pathname === '/app') {
    return (
      <QueryClientProvider client={publicQueryClient}>
        <Routes>
          <Route path="/proposals/public/:token" element={<PublicProposalPage />} />
          <Route path="/pay/:token" element={<PublicInvoicePage />} />
          <Route path="/app" element={<AppDownloadPage />} />
        </Routes>
      </QueryClientProvider>
    );
  }
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }
  return (
    <ErrorBoundary key={privacyEpoch}>
      <Shell>
        <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        <Route path="/engagements" element={<EngagementsPage />} />
        <Route path="/my-work" element={<MyWorkPage />} />
        <Route path="/meetings" element={<MeetingsPage />} />
        {/* Meeting detail is a modal on /meetings — deep links (push taps) land on the list. */}
        <Route path="/meetings/:id" element={<Navigate to="/meetings" replace />} />
        <Route path="/hub" element={<HubPage />} />
        <Route path="/engagements/:id" element={<EngagementDetailPage />} />
        <Route path="/proposals" element={<ProposalsPage />} />
        <Route path="/proposals/new" element={<ProposalComposerPage />} />
        <Route path="/proposals/:id" element={<ProposalDetailPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="/compliance" element={<CompliancePage />} />
        <Route path="/compliance/:id" element={<ComplianceItemDetailPage />} />
        <Route path="/time" element={<TimeEntriesPage />} />
        <Route path="/goals" element={<GoalsPage />} />
        <Route path="/tags" element={<Navigate to="/admin/tags" replace />} />
        <Route path="/webhooks" element={<Navigate to="/admin/webhooks" replace />} />
        <Route path="/automations" element={<Navigate to="/admin/automations" replace />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/team" element={<TeamsPage />} />
        <Route path="/attendance" element={<AttendancePage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AccessControlPage />} />
          <Route path="notifications" element={<NotificationPolicyPage />} />
          <Route path="dashboard" element={<DashboardBuilderPage />} />
          <Route path="goals" element={<GoalConfigPage />} />
          <Route path="automations" element={<AutomationsPage />} />
          <Route path="webhooks" element={<WebhooksPage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="trash" element={<TrashPage />} />
          <Route path="tags" element={<TagsPage />} />
          <Route path="templates" element={<TemplatesPage />} />
          <Route path="clauses" element={<ClauseLibraryPage />} />
          <Route path="catalog" element={<FreeZoneCatalogPage />} />
          <Route path="invoice-branding" element={<InvoiceBrandingPage />} />
          <Route path="proposal-branding" element={<ProposalBrandingPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </Shell>
    </ErrorBoundary>
  );
}
